import { getDomain, getEnvironment, isDev } from '@library/environment/isomorphic'
import { composeFetch, type RequestConfig } from '@library/http'
import {
  addPath,
  composeRequest,
  goustoHeaders,
  setHeader,
  setHost,
  setMethod,
} from '@library/http/requests'

import type {
  DashboardExperienceOrderDeliveryDetailsResponse,
  DashboardExperienceResponse,
  UpdateDashboardExperienceOrderRequest,
} from './interfaces'

const LOCAL_API_HOST = 'https://staging-api.gousto.info'

function getActivateApiHost() {
  if (isDev()) {
    return LOCAL_API_HOST
  }

  return `https://${getEnvironment()}-api.${getDomain()}`
}

const normaliseAvailableDeliveryDays = (
  availableDeliveryDays: DashboardExperienceOrderDeliveryDetailsResponse['available_delivery_days'],
): DashboardExperienceOrderDeliveryDetailsResponse['available_delivery_days'] => {
  if (!availableDeliveryDays) {
    return availableDeliveryDays
  }

  return Object.fromEntries(
    Object.entries(availableDeliveryDays).map(([date, deliveryDay]) => {
      const {
        is_default: snakeCaseIsDefault,
        core_day_id: snakeCaseCoreDayId,
        unavailable_reason: snakeCaseUnavailableReason,
        alternate_delivery_day: snakeCaseAlternateDeliveryDay,
        slots,
        ...restDeliveryDay
      } = deliveryDay as typeof deliveryDay & {
        is_default?: boolean
        core_day_id?: string
        unavailable_reason?: string
        alternate_delivery_day?: string | null
      }

      return [
        date,
        {
          ...restDeliveryDay,
          isDefault: deliveryDay.isDefault ?? snakeCaseIsDefault ?? false,
          coreDayId: deliveryDay.coreDayId ?? snakeCaseCoreDayId ?? '',
          unavailableReason: deliveryDay.unavailableReason ?? snakeCaseUnavailableReason ?? '',
          alternateDeliveryDay:
            deliveryDay.alternateDeliveryDay ?? snakeCaseAlternateDeliveryDay ?? null,
          slots: slots.map((slot) => {
            const {
              day_slot_lead_time_id: snakeCaseDaySlotLeadTimeId,
              core_slot_id: snakeCaseCoreSlotId,
              delivery_start_time: snakeCaseDeliveryStartTime,
              delivery_end_time: snakeCaseDeliveryEndTime,
              delivery_price: snakeCaseDeliveryPrice,
              when_cutoff: snakeCaseWhenCutoff,
              default_day: snakeCaseDefaultDay,
              day_slot_lead_time_is_express: snakeCaseDaySlotLeadTimeIsExpress,
              is_default: snakeCaseSlotIsDefault,
              cutoff_day: snakeCaseCutoffDay,
              cutoff_time: snakeCaseCutoffTime,
              ...restSlot
            } = slot as typeof slot & {
              day_slot_lead_time_id?: string
              core_slot_id?: string
              delivery_start_time?: string
              delivery_end_time?: string
              delivery_price?: string
              when_cutoff?: string
              default_day?: number
              day_slot_lead_time_is_express?: boolean
              is_default?: boolean
              cutoff_day?: number
              cutoff_time?: string
            }

            return {
              ...restSlot,
              daySlotLeadTimeId: slot.daySlotLeadTimeId ?? snakeCaseDaySlotLeadTimeId ?? '',
              coreSlotId: slot.coreSlotId ?? snakeCaseCoreSlotId ?? '',
              deliveryStartTime: slot.deliveryStartTime ?? snakeCaseDeliveryStartTime ?? '',
              deliveryEndTime: slot.deliveryEndTime ?? snakeCaseDeliveryEndTime ?? '',
              deliveryPrice: slot.deliveryPrice ?? snakeCaseDeliveryPrice ?? '',
              whenCutoff: slot.whenCutoff ?? snakeCaseWhenCutoff ?? '',
              defaultDay: slot.defaultDay ?? snakeCaseDefaultDay ?? 0,
              daySlotLeadTimeIsExpress:
                slot.daySlotLeadTimeIsExpress ?? snakeCaseDaySlotLeadTimeIsExpress ?? false,
              isDefault: slot.isDefault ?? snakeCaseSlotIsDefault ?? false,
              cutoffDay: slot.cutoffDay ?? snakeCaseCutoffDay ?? 0,
              cutoffTime: slot.cutoffTime ?? snakeCaseCutoffTime ?? '',
            }
          }),
        },
      ]
    }),
  )
}

const parseDashboardExperience = async (
  response: Response,
): Promise<DashboardExperienceResponse> => {
  if (!response.ok) {
    const responseMessage = await response.text()
    throw new Error(`service responded with ${response.status}: ${responseMessage}`)
  }

  return (await response.json()) as DashboardExperienceResponse
}

const parseOrderDeliveryDetails = async (
  response: Response,
): Promise<DashboardExperienceOrderDeliveryDetailsResponse> => {
  if (!response.ok) {
    const responseMessage = await response.text()
    throw new Error(`service responded with ${response.status}: ${responseMessage}`)
  }

  const orderDeliveryDetails =
    (await response.json()) as DashboardExperienceOrderDeliveryDetailsResponse

  return {
    ...orderDeliveryDetails,
    available_delivery_days: normaliseAvailableDeliveryDays(
      orderDeliveryDetails.available_delivery_days,
    ),
  }
}

const parseUpdateDashboardExperienceOrder = async (response: Response): Promise<null> => {
  if (!response.ok) {
    const responseMessage = await response.text()

    if (
      response.status === 500 &&
      (responseMessage.trim() === '' || responseMessage.trim() === 'Internal Server Error')
    ) {
      return null
    }

    throw new Error(`service responded with ${response.status}: ${responseMessage}`)
  }

  return null
}

const parseSkipOrderResponse = async (response: Response): Promise<null> => {
  if (!response.ok) {
    const responseMessage = await response.text()
    throw new Error(`service responded with ${response.status}: ${responseMessage}`)
  }

  return null
}

export const fetchDashboardExperience = composeFetch(
  composeRequest(setHost(getActivateApiHost), addPath('activate/dashboard/v1'), goustoHeaders),
  parseDashboardExperience,
)

export const fetchOrderDeliveryDetails = composeFetch(
  composeRequest<string>(
    setHost(getActivateApiHost),
    addPath('activate/customerengagement/dashboard/order_delivery_details/v1'),
    addPath((orderId) => orderId),
    goustoHeaders,
  ),
  parseOrderDeliveryDetails,
)

export const updateDashboardExperienceOrder = composeFetch(
  composeRequest<{ orderId: string; body: UpdateDashboardExperienceOrderRequest }>(
    setHost(getActivateApiHost),
    addPath('activate/customerengagement/dashboard/update-order/v1'),
    addPath(({ orderId }) => orderId),
    setHeader('Content-Type', 'application/json'),
    setMethod('PUT'),
    (
      request: RequestConfig,
      input: { orderId: string; body: UpdateDashboardExperienceOrderRequest },
    ) => {
      const requestBody = JSON.stringify(input.body)

      request.body = requestBody

      if (isDev()) {
        // Surface the exact request payload in local development when the backend returns opaque 500s.
        // eslint-disable-next-line no-console
        console.info('updateDashboardExperienceOrder request body', JSON.parse(requestBody))
      }

      return request
    },
    goustoHeaders,
  ),
  parseUpdateDashboardExperienceOrder,
)

export const skipOrder = composeFetch(
  composeRequest<string>(
    setHost(getActivateApiHost),
    addPath('activate/customerengagement/dashboard/skip_order/v1'),
    addPath((orderId) => orderId),
    setMethod('DELETE'),
    goustoHeaders,
  ),
  parseSkipOrderResponse,
)
